import React, { useEffect, useMemo, useState } from 'react';
import { BackLink } from 'components/back-link';
import { useSessionContext } from 'contexts/session';
import { Select, Text } from '@perch-labs/shrike';
import { LoadingPage } from 'components/loading';
import { ErrorPage } from 'components/errors';
import { copyFor, routes } from 'config';
import { Container, Page } from 'styles';
import { MAX_SECONDARY_PAGE_WIDTH } from 'domain/style';
import { useUser } from 'hooks/use-user.hook';
import { useContractAgreements } from 'hooks/use-contract-agreements.hook';
import {
  MappedContractSubscriberRequirement,
  useContractRequirements,
} from 'hooks/use-contract-requirements.hook';
import {
  RedirectReason,
  useFunnelRedirect,
} from 'hooks/use-funnel-redirect.hook';
import { AccountPlan, useAccountPlans } from 'hooks/use-account-plans.hook';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { useAccountContext } from 'contexts/account';
import { useGeneralTerms } from 'hooks/use-general-terms.hook';
import { GroupHeader, SignTermsButton, StyledSpacer } from './contracts.style';
import { Contract } from './contract.component';
import { IncompleteContract } from './incomplete-contract.component';
import { SignTermsModal } from './sign-terms-modal.component';
import { findUtilityById } from '../lmi-eligibility/check-lmi-eligibility.utils';
import { DisclosureModal } from './disclosure-modal/disclosure-modal.component';
import { fetchSanitizedUtilityAccounts } from '../fetch-sanitized-utility-accounts.utils';
import {
  getPersonalizedContractEnvelopes,
  getUtilityAccountPersonalizedContracts,
} from './contracts.page.utils';

const { settings } = routes();
const getCopy = copyFor('settings.contractAgreements');

export const Contracts = () => {
  const { userId } = useSessionContext();
  const {
    data,
    error,
    loading,
    refetch: refetchAgreements,
  } = useContractAgreements();

  const {
    data: reqData,
    error: reqError,
    loading: reqLoading,
    refetch: refetchRequirements,
  } = useContractRequirements();

  const {
    data: generalTermsData,
    loading: generalTermsLoading,
    error: generalTermsError,
  } = useGeneralTerms();

  const [showSignTermsModal, setShowSignTermsModal] = useState(false);
  const { data: user, refetch: userRefetch } = useUser();
  const [language, setLanguage] = useState<any>(user?.languageShortCode);
  const { customerType } = useAccountContext();
  const isSMB = customerType === 'smb';
  const [showDisclosureModal, setShowDisclosureModal] = useState(false);
  const [isDisclosureState, setIsDisclosureState] = useState(false);
  const [shouldShowDisclosureButton, setShouldShowDisclosureButton] =
    useState(false);
  const [disclosureContract, setDisclosureContract] =
    useState<MappedContractSubscriberRequirement>();
  const [pcForUtilityAccount, setPcForUtilityAccount] = useState<any>(null);
  const [isFirstRequest, setIsFirstRequest] = useState(false);
  const [disclosureStatus, setDisclosureStatus] = useState<
    string | null | undefined
  >(null);
  const [selectedUtilityAccount, setSelectedUtilityAccount] =
    useState<number>(0);
  const [selectedUtility, setSelectedUtility] = useState<number | null>(0);
  const [contractType, setContractType] = useState<string | null>(null);
  const ldClient = useLDClient();
  let incomplete: MappedContractSubscriberRequirement[] = [];
  let completedAgreements: any[] = [];
  let removedAgreement: any[] = [];
  let createCompletedAgreements: any;

  const {
    data: planData,
    loading: planLoading,
    error: planError,
  } = useAccountPlans(0, 'active');

  const plans = planData?.plans as AccountPlan[];
  const sanitizedUtilityAccounts = useMemo(() => {
    if (!plans) return [];
    return fetchSanitizedUtilityAccounts(plans);
  }, [plans]);

  const utilityAccountPersonalizedContracts = useMemo(() => {
    if (!plans) return [];
    return getUtilityAccountPersonalizedContracts(plans);
  }, [plans]);

  const custom = useMemo(
    () => ({
      funnel: user?.originationSource?.funnel ?? 'unknown',
      state: planData?.plans
        .flatMap(plan => plan.utilityAccounts)
        .find(ua => ua.utility.id === selectedUtility)
        ?.utility.state.abbreviation.toLowerCase(),
      utilityId: selectedUtility!,
    }),
    [user?.originationSource?.funnel, selectedUtility, planData?.plans]
  );

  useEffect(() => {
    if (ldClient && custom.utilityId && custom.state) {
      ldClient.identify({
        kind: 'user',
        key: 'user_key',
        ...custom,
      });
    }
  }, [ldClient, custom]);

  const flag = useFlags();

  useEffect(() => {
    if (sanitizedUtilityAccounts?.length > 0) {
      setSelectedUtilityAccount(sanitizedUtilityAccounts?.[0]?.value);
      setSelectedUtility(sanitizedUtilityAccounts?.[0]?.utilityId);
    }
  }, [sanitizedUtilityAccounts]);

  useEffect(() => {
    if (isDisclosureState && selectedUtilityAccount) {
      setPcForUtilityAccount(
        utilityAccountPersonalizedContracts.find(
          utilityAccount =>
            utilityAccount.utilityAccountId === selectedUtilityAccount
        )
      );
    }
  }, [selectedUtilityAccount]);

  useEffect(() => {
    if (
      Array.isArray(pcForUtilityAccount?.personalizedContracts) &&
      pcForUtilityAccount.personalizedContracts.length === 0 &&
      !isFirstRequest
    ) {
      setIsFirstRequest(true);
    } else {
      setIsFirstRequest(false);
    }
  }, [pcForUtilityAccount]);

  const firstDocuSignUtilityAccountId = reqData?.find(
    r => r.status === 'pending' && r.signatureType === 'docusign'
  )?.utilityAccountId;
  const [redirect, { loading: redirectLoading }] = useFunnelRedirect({
    idParams: {
      utilityAccountId: firstDocuSignUtilityAccountId,
    },
    userId,
    reason: RedirectReason.VnmAvailable,
  });

  useEffect(() => {
    if (flag.ffEnableContractForm) {
      setIsDisclosureState(false);
    } else {
      const disclosureEnabledStates = flag.disclosureEnabledStates || [];
      setIsDisclosureState(
        disclosureEnabledStates.some(state => {
          return planData?.plans
            .map(plan => plan.utilityAccounts[0]?.utility.state.abbreviation)
            .includes(state);
        })
      );
    }
  }, [flag, planData]);

  useEffect(() => {
    if (user && language !== user.languageShortCode) {
      setLanguage(user.languageShortCode);
    }
  }, [user]);

  useEffect(() => {
    if (reqData && language && refetchRequirements) {
      refetchRequirements();
    }
  }, [language]);

  useEffect(() => {
    if (reqData && disclosureContract === undefined) {
      const foundContract = reqData.find(
        r => r.signatureType === 'docusign' && r.contractType !== 'lmi'
      );
      if (foundContract) {
        setDisclosureContract(foundContract);
        setContractType(foundContract?.contractType);
      }
    }
  }, [reqData, disclosureContract]);

  useEffect(() => {
    if (disclosureContract && isDisclosureState) {
      setShouldShowDisclosureButton(true);
    }
  }, [disclosureContract]);

  const fetchPcForUtilityAccount = (utilityAccountId: number) => {
    return utilityAccountPersonalizedContracts.find(
      utilityAccount => utilityAccount.utilityAccountId === utilityAccountId
    );
  };

  useEffect(() => {
    const checkForDisclosureStatus = () => {
      setDisclosureStatus(null);
      const personalizedContracts = fetchPcForUtilityAccount(
        selectedUtilityAccount
      )?.personalizedContracts;
      // check if personalizedContractID is in personalizedContracts and return status
      if (!user) return;
      const disclosureStatus = personalizedContracts?.map(pc => {
        const status = getPersonalizedContractEnvelopes(user)?.find(
          envelope =>
            Number(envelope?.personalizedContractId) === Number(pc?.id)
        )?.status;
        return status;
      });
      if (disclosureStatus) {
        setDisclosureStatus(disclosureStatus[0]);
      }
    };
    if (selectedUtilityAccount && utilityAccountPersonalizedContracts) {
      checkForDisclosureStatus();
    }
  }, [selectedUtilityAccount]);

  // After submitting terms we want to reload this data.
  const refetchAll = async () => {
    Promise.all([refetchAgreements(), refetchRequirements(), userRefetch()]);
  };
  if (loading || reqLoading || planLoading || generalTermsLoading)
    return <LoadingPage />;
  if (error || reqError || planError || !data || generalTermsError) {
    return <ErrorPage title={getCopy('title')} to={settings} />;
  }

  const displayFileUrl = contractAgreement => {
    const { contractVersionLanguage, contractVersion } = contractAgreement;
    return (
      contractAgreement.fileUrl ||
      contractVersionLanguage?.fileUrl ||
      contractVersion.fileUrl
    );
  };

  completedAgreements = data.map(agreement => ({
    addendums: agreement.contractVersion.addendums,
    createdAt: agreement.createdAt,
    displayName: agreement.contractVersion.displayName,
    executedContracts: agreement.executedContracts,
    fileUrl: displayFileUrl(agreement),
    contractVersionId: agreement.contractVersion.id,
    id: agreement.id,
  }));

  const isNonMNLmiUser =
    user?.originationSource?.funnel !== 'mnLmi' &&
    user?.originationSource?.enrollmentExperience !== 'partner_provided';

  incomplete = reqData?.filter(r => r.status === 'pending') || [];
  if (
    isDisclosureState &&
    disclosureStatus !== 'completed' &&
    disclosureContract?.signatureType === 'docusign'
  ) {
    // This is a hack for the MN LMI users, if the user opts for partner_provided option
    // and chooses lmi , they will have the contract generated as lmi contract(Both lmi and usage_request combined)
    // and the disclosure contract will be missing in the GetUser query. The isNonMNLmiUser check is to make sure that the
    // disclosure contract is not added to the incomplete array for the MN LMI users. So such users does not
    // require to sign the disclosure contract again. But for regular cases where the document is not signed , the requirements
    // will ensure that the disclosure is added to the incomplete array.
    if (
      !incomplete.find(
        agreement => agreement.displayName === disclosureContract.displayName
      ) &&
      isNonMNLmiUser
    ) {
      incomplete = [...incomplete, disclosureContract];
    }
    removedAgreement = completedAgreements.find(
      agreement => agreement.displayName === disclosureContract.displayName
    );
    completedAgreements = completedAgreements.filter(
      agreement => agreement.displayName !== disclosureContract.displayName
    );
  } else {
    const disclosureDisplayName = disclosureContract?.displayName;
    if (
      !completedAgreements.find(
        agreement => agreement.displayName === disclosureDisplayName
      )
    ) {
      completedAgreements = [...completedAgreements, removedAgreement];
    }
  }

  // We are seeing instances where the old users are requesting to be
  // enrolled into the vnm projects and most of them are missing the
  // privacy and general terms contracts. We are adding these contracts
  // to the incomplete array so that the user can sign them.
  const contractIds = {
    privacyTerms: 31,
    generalTerms: 43,
    agencyTerms: 30,
  };

  const NO_AGENCY_STATES = ['IL', 'MA', 'OR'];
  const stateAbbreviation =
    plans
      .flatMap(plan => plan.utilityAccounts)
      .find(account => account.id === selectedUtilityAccount)?.utility.state
      .abbreviation || '';

  if (data && generalTermsData) {
    const privacyTerms = data.find(
      agreement =>
        agreement.contractVersion.contractId === contractIds.privacyTerms
    );

    const generalTerms = data.find(
      agreement =>
        agreement.contractVersion.contractId === contractIds.generalTerms
    );

    // States can have their own version of the agency terms, so we are changing the check to contractType
    const agencyTerms = data.find(
      agreement => agreement.contractVersion.contractType === 'vnm'
    );

    if (!privacyTerms) {
      const privacyTermsContract =
        generalTermsData.generalContractVersions.find(
          g => g.contractId === contractIds.privacyTerms
        );
      if (privacyTermsContract) {
        const updatedPrivacyTerms = {
          ...privacyTermsContract,
          contractVersionId: privacyTermsContract.id,
        };
        incomplete.push(updatedPrivacyTerms);
      }
    }
    if (!generalTerms) {
      const generalTermsContract =
        generalTermsData.generalContractVersions.find(
          g => g.contractId === contractIds.generalTerms
        );
      if (generalTermsContract) {
        const updatedGeneralTerms = {
          ...generalTermsContract,
          contractVersionId: generalTermsContract.id,
        };
        incomplete.push(updatedGeneralTerms);
      }
    }
    if (!agencyTerms && !NO_AGENCY_STATES.includes(stateAbbreviation)) {
      const agencyTermsContract = generalTermsData.generalContractVersions.find(
        g => g.contractId === contractIds.agencyTerms
      );
      if (agencyTermsContract) {
        const updatedAgencyTerms = {
          ...agencyTermsContract,
          contractVersionId: agencyTermsContract.id,
        };
        incomplete.push(updatedAgencyTerms);
      }
    }
  }

  /**
   * Since there is no direct way of fetching the completed disclosure contract
   * through the userSigned API, we are instead fetching the completed disclosure contract
   * from the GetUser query and checking if the status is completed. Then we are creating a
   * completedAgreements object with the completed disclosure contract and adding it to the
   * completedAgreements array, so that when user switches between utility accounts, the completed
   * disclosure contract is displayed.
   */
  if (selectedUtilityAccount && pcForUtilityAccount) {
    const personalizedContracts = fetchPcForUtilityAccount(
      selectedUtilityAccount
    )?.personalizedContracts;
    const disclosureCompletedFile = personalizedContracts?.map(pc => {
      if (!user) return;
      const completedFile = getPersonalizedContractEnvelopes(user)?.find(
        envelope => Number(envelope?.personalizedContractId) === Number(pc?.id)
      );
      return completedFile;
    });

    if (
      disclosureCompletedFile &&
      disclosureCompletedFile[0]?.status === 'completed'
    ) {
      createCompletedAgreements = {
        createdAt: disclosureCompletedFile?.[0]?.envelopeUpdatedAt || null,
        displayName: disclosureContract?.displayName || '',
        fileUrl: disclosureCompletedFile?.[0]?.completedFile || '',
        executedContracts: [],
        addendums: [],
        contractVersionId: disclosureContract?.contractVersionId,
        id: personalizedContracts?.find(
          c => c?.utilityAccountId === selectedUtilityAccount
        )?.contractVersionId,
      };

      if (
        !completedAgreements.find(
          c => c.contractVersionId === disclosureContract?.contractVersionId
        )
      ) {
        completedAgreements = [
          ...completedAgreements,
          createCompletedAgreements,
        ];
      }
    }
  }

  const incompleteCheckboxContracts = incomplete.filter(
    r => r.signatureType === 'agreement'
  );

  const incompleteDocusigns = incomplete.filter(
    r => r.signatureType === 'docusign'
  );

  completedAgreements = completedAgreements.flat();
  const onSignTermsClick = () => {
    // If there are incomplete checkbox contracts, show the modal.
    // Otherwise, just redirect straight to the funnel for docusign
    if (incompleteCheckboxContracts.length) {
      setShowSignTermsModal(true);
    } else {
      redirect();
    }
  };

  const hasIncompleteDisclosure =
    isDisclosureState && incomplete.find(r => r.contractType === 'disclosure');

  const handleDisplayDisclosureModal = () => {
    setShowDisclosureModal(!showDisclosureModal);
  };

  return (
    <>
      <BackLink to={settings} />
      <Page maxWidth={MAX_SECONDARY_PAGE_WIDTH}>
        <Text tag="h1" textStyle="heading800" margin={{ bottom: '48px' }}>
          {getCopy('title')}
        </Text>
        {!isSMB && isDisclosureState ? (
          <Container>
            <Select
              label="Utility Account"
              name="utilityDropdown"
              options={sanitizedUtilityAccounts}
              onChange={value => {
                setSelectedUtilityAccount(Number(value));
                setIsFirstRequest(false);
                setSelectedUtility(
                  findUtilityById(sanitizedUtilityAccounts, Number(value))
                    ?.utilityId || null
                );
              }}
              disabled={sanitizedUtilityAccounts?.length === 1}
            />
          </Container>
        ) : null}
        {!!incomplete.length && (
          <GroupHeader>
            <Text
              tag="h2"
              color="error"
              textStyle="heading600"
              margin={{ bottom: '8px' }}
            >
              {getCopy('incomplete')}
            </Text>
            {!hasIncompleteDisclosure ? (
              <SignTermsButton
                onClick={onSignTermsClick}
                loading={redirectLoading}
              >
                {getCopy('openSignTerms')}
              </SignTermsButton>
            ) : null}
          </GroupHeader>
        )}
        {incomplete.map(r => (
          <IncompleteContract
            key={r.contractVersionId}
            requirement={r}
            isDisclosureState={isDisclosureState}
            disclosureContract={disclosureContract}
            handleDisplayDisclosureModal={handleDisplayDisclosureModal}
          />
        ))}
        <StyledSpacer incomplete={incomplete.length > 0} />
        {!!completedAgreements.length && (
          <GroupHeader>
            <Text tag="h2" textStyle="heading600">
              Completed
            </Text>
          </GroupHeader>
        )}
        {completedAgreements.map(agreement => {
          const {
            createdAt,
            addendums,
            displayName,
            executedContracts,
            fileUrl,
            contractVersionId,
            id,
          } = agreement;
          if (executedContracts?.length) {
            return (
              <div key={id}>
                {!isSMB && isDisclosureState && shouldShowDisclosureButton
                  ? executedContracts?.map(executedContract => (
                      <Contract
                        createdAt={createdAt}
                        displayName={displayName}
                        details={`Solar Farm: ${executedContract.vnmSolarProject.name}`}
                        fileUrl={executedContract.fileUrl}
                        disclosureContract={disclosureContract}
                        handleDisplayDisclosureModal={
                          handleDisplayDisclosureModal
                        }
                        contractVersionId={contractVersionId}
                        id={executedContract.id}
                        key={executedContract.id}
                      />
                    ))
                  : executedContracts.map(executedContract => (
                      <Contract
                        createdAt={createdAt}
                        displayName={displayName}
                        details={`Solar Farm: ${executedContract.vnmSolarProject.name}`}
                        fileUrl={executedContract.fileUrl}
                        id={executedContract.id}
                        key={executedContract.id}
                      />
                    ))}
              </div>
            );
          }
          return (
            <div key={id}>
              {!isSMB && isDisclosureState && shouldShowDisclosureButton ? (
                <Contract
                  createdAt={createdAt}
                  displayName={displayName}
                  disclosureContract={disclosureContract}
                  handleDisplayDisclosureModal={handleDisplayDisclosureModal}
                  // show signed contract if it exists, otherwise just show contract
                  fileUrl={fileUrl}
                  contractVersionId={contractVersionId}
                  id={id}
                />
              ) : (
                <Contract
                  createdAt={createdAt}
                  displayName={displayName}
                  // show signed contract if it exists, otherwise just show contract
                  fileUrl={fileUrl}
                  id={id}
                />
              )}
              {addendums &&
                addendums.map(addendum => (
                  <Contract
                    displayName="Solar Developer Signature Page"
                    fileUrl={addendum.fileUrl}
                    id={addendum.id}
                    key={addendum.id}
                  />
                ))}
            </div>
          );
        })}
      </Page>
      {showSignTermsModal && (
        <SignTermsModal
          incompleteRequirements={incompleteCheckboxContracts}
          redirect={incompleteDocusigns.length ? redirect : undefined}
          userId={userId}
          onClose={() => setShowSignTermsModal(false)}
          onSubmit={refetchAll}
        />
      )}
      {showDisclosureModal ? (
        <DisclosureModal
          close={() => setShowDisclosureModal(false)}
          isFirstRequest={isFirstRequest}
          utilityDropDown={sanitizedUtilityAccounts.filter(
            utilityAccount => utilityAccount.value === selectedUtilityAccount
          )}
          refetch={refetchAll}
          utilityAccountPersonalizedContracts={
            utilityAccountPersonalizedContracts
          }
          user={user}
          disclosureStatus={disclosureStatus}
          contractType={contractType}
        />
      ) : null}
    </>
  );
};
